import classNames from 'classnames'
import { Minus, Xmark } from 'iconoir-react'
import { useContext, useEffect, type ReactElement } from 'react'
import { SchedulingDockContext } from './SchedulingDockContext'
import { buildBookingDraft, firstNameOf } from 'app/lib/bookingDraft'
import { ScheduleAppointmentContent } from 'app/components/scheduling/inapp/ScheduleAppointmentContent'
import type { BookingDetails } from 'app/components/scheduling/inapp/BookingReporter'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { useIsMobile } from 'app/hooks/useMediaQuery'

/**
 * Panel height. At 860 the dock gives the wizard's 620px step region nearly
 * all the room it wants, so the scroll wrapper barely has to engage, while
 * `max-h-[calc(100vh-3rem)]` below still clamps it down gracefully on short
 * viewports.
 */
const DOCK_HEIGHT = 860

/**
 * Panel width, and the collapsed bar width, published as
 * --scheduling-dock-width so MessageAlertStack can keep its row clear of the
 * dock horizontally (see that file). Tailwind arbitrary-value classes must be
 * static strings, so the JSX below repeats these as literal `w-[760px]` /
 * `w-[320px]` - keep them equal to DOCK_WIDTH / BAR_WIDTH if either changes.
 */
const DOCK_WIDTH = 760
const BAR_WIDTH = 320

/**
 * The scheduling widget, docked bottom right of /inbox. Sits at z-[45]: above the
 * new-message alert stack (z-40) and below Radix dialogs (z-50), so a confirmation
 * opened from inside the dock lands on top of it rather than behind it.
 *
 * Minimizing hides the body with CSS and keeps the wizard mounted. Unmounting it
 * would reset the wizard to step 1 and silently discard the staff member's
 * selections, which is the whole reason minimize exists.
 */
export function SchedulingDock(): ReactElement | null {
  const { dockContact, minimized, setMinimized, closeDock, requestDraft, dockLocation } =
    useContext(SchedulingDockContext)
  const isMobile = useIsMobile()

  const width = minimized ? BAR_WIDTH : DOCK_WIDTH

  // Published so MessageAlertStack can keep its row clear of the dock
  // horizontally. A CSS variable rather than shared state: the stack is
  // mounted globally in AppLayout and must not depend on inbox-scoped context.
  useEffect(() => {
    if (!dockContact || isMobile) return
    const root = document.documentElement
    root.style.setProperty(`--scheduling-dock-width`, `${width}px`)
    return () => {
      root.style.removeProperty(`--scheduling-dock-width`)
    }
  }, [dockContact, width, isMobile])

  // Guards the tab close only. The app mounts BrowserRouter, not a data router,
  // so useBlocker is unavailable and in-app navigation cannot be intercepted.
  useEffect(() => {
    if (!dockContact || isMobile) return
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ``
    }
    window.addEventListener(`beforeunload`, onBeforeUnload)
    return () => window.removeEventListener(`beforeunload`, onBeforeUnload)
  }, [dockContact, isMobile])

  // Below md the dock is replaced by the full-screen dialog, rendered by the
  // contact card itself. Nothing to draw here.
  if (!dockContact || isMobile) return null

  const handleBooked = (details: BookingDetails) => {
    requestDraft(
      buildBookingDraft({
        firstName: firstNameOf(dockContact.name),
        dayLabel: details.dayLabel,
        timeLabel: details.timeLabel,
        providerName: details.providerName,
      }),
      dockContact.cellPhone,
    )
  }

  return (
    <div
      id="scheduling-dock"
      data-testid="scheduling-dock"
      className={classNames(
        `fixed bottom-4 right-4 z-[45] flex flex-col overflow-hidden rounded-xl border border-default bg-surface-raised shadow-elevation-8`,
        `max-h-[calc(100vh-3rem)] max-w-[calc(100vw-2rem)]`,
        // w-[320px] == BAR_WIDTH, h-[860px] == DOCK_HEIGHT, w-[760px] == DOCK_WIDTH.
        minimized ? `w-[320px]` : `h-[860px] w-[760px]`,
      )}
    >
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-default bg-surface-subtle px-4">
        {minimized ? (
          // Only interactive while minimized: this is the sole way to restore
          // the dock in that state. Expanded, the title has no action of its
          // own, so it renders as static text instead of a button that does
          // nothing on Enter/Space and announces as "Scheduling, button".
          <button
            type="button"
            aria-label="Restore scheduling"
            onClick={() => setMinimized(false)}
            className="min-w-0 flex-1 truncate text-left text-body-sm font-semibold text-strong"
          >
            Schedule · {dockContact.name}
          </button>
        ) : (
          <span className="min-w-0 flex-1 truncate text-body-sm font-semibold text-strong">
            Schedule · {dockContact.name}
          </span>
        )}
        {!minimized && (
          <TooltipWrapper placement="bottom-end" message="Minimize">
            <button
              type="button"
              aria-label="Minimize scheduling"
              onClick={() => setMinimized(true)}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-subtle transition-colors hover:bg-surface-muted hover:text-default"
            >
              <Minus className="h-5 w-5" strokeWidth={2} />
            </button>
          </TooltipWrapper>
        )}
        <TooltipWrapper placement="bottom-end" message="Close">
          <button
            type="button"
            aria-label="Close scheduling"
            onClick={closeDock}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-subtle transition-colors hover:bg-surface-muted hover:text-default"
          >
            <Xmark className="h-5 w-5" strokeWidth={2} />
          </button>
        </TooltipWrapper>
      </div>

      <div
        data-testid="scheduling-dock-body"
        className={classNames(`flex min-h-0 grow flex-col overflow-hidden p-4`, minimized && `hidden`)}
      >
        {/* key is load-bearing, not decoration. In the dock `active` is
            permanently true, so the reset effect inside the content component
            (which only runs when `active` goes false) never fires, and
            SchedulingWizard's own step/appointment-type/provider/slot state
            lives in a provider nested inside it. Without a remount, switching
            the dock to a different contact keeps the previous patient
            selection, skips the "Who is this appointment for?" picker, and can
            submit a booking under the wrong patient. */}
        <ScheduleAppointmentContent
          key={dockContact.id}
          contact={dockContact}
          // Non-null: Task 8 only opens the dock for contacts that carry a
          // locationId, so this is always populated by the time the dock renders.
          locationId={dockContact.locationId!}
          // dockLocation, not the live location: the live one follows whatever
          // location the inbox is currently scoped to and can move to a
          // different location (e.g. via a MessageAlertCard click) while this
          // dock stays open for its original contact. Reading the live value
          // here could resolve provider names against the wrong location's
          // Kolla vendor ids, since ids like provider_3 repeat across locations.
          kollaConsumerId={dockLocation?.kollaConsumerId ?? undefined}
          active
          showHeader={false}
          onBooked={handleBooked}
        />
      </div>
    </div>
  )
}
